import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common";
import Crumbs from "@/components/Crumbs";
import { saveCurrentScroll, useScrollRestore } from "@/lib/scrollRestore";
import { fmtDateTime } from "@/lib/format";
import { extractDateFromText, isSameLocalDay } from "@/lib/dateFromText";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AppModal, AppModalPrimaryButton, AppModalSecondaryButton } from "@/components/ui/AppModal";
import DivergenciaDataDialog from "@/components/DivergenciaDataDialog";
import { Plus, CheckCircle2, Circle, Loader2, CalendarX2, User, CalendarClock, ArrowRight, Megaphone, MessageSquare } from "@/components/icons";

// Tipos para NOVAS atividades — "Próximo contato" e "Retorno" saíram
// (ver PEDIDO_EMERGENT_REGISTRAR_ATIVIDADE.md item 3). Legado com esses
// tipos continua sendo exibido como está.
const TIPOS = ["Ligação", "Reunião", "Visita", "WhatsApp", "E-mail", "Teste/Demonstração", "Enviar proposta"];
const INPUT = "w-full h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Section({ title, items, tone, onOpen }) {
  if (!items.length) return null;
  return (
    <div className="mb-6">
      <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${tone}`}>{title} ({items.length})</h3>
      <div className="space-y-2">
        {items.map((a) => (
          <button
            key={a.id}
            onClick={() => onOpen(a)}
            data-testid={`ativ-${a.id}`}
            className="group w-full text-left bg-card rounded-xl border border-black/5 p-4 flex items-center gap-3 transition-all hover:border-brand/30 hover:shadow-[0_6px_20px_rgb(0,138,176,0.08)]"
          >
            {a.concluida ? <CheckCircle2 className="w-5 h-5 text-ok shrink-0" /> : <Circle className="w-5 h-5 text-slate-300 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className={`font-medium text-navy ${a.concluida ? "line-through text-slate-400" : ""}`}>{a.titulo}</div>
              <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                <span className="font-medium text-slate-500">{a.tipo}</span>
                {a.cliente_nome ? <><span className="text-slate-300">·</span><span>{a.cliente_nome}</span></> : null}
                <span className="text-slate-300">·</span><span>{fmtDateTime(a.data)}</span>
                {a.owner_name ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-soft text-brand-hover font-semibold" data-testid={`ativ-owner-${a.id}`}>
                    <User className="w-3 h-3" />{a.owner_name}
                  </span>
                ) : null}
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-brand shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Atividades() {
  const { user } = useAuth();
  const isManager = ["admin", "director", "manager", "diretor_geral"].includes(user?.role);
  const [data, setData] = useState(null);
  const [team, setTeam] = useState(null);
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") === "equipe" ? "equipe" : "minha";
  const setTab = (v) => {
    const next = new URLSearchParams(sp);
    next.set("tab", v);
    setSp(next, { replace: true });
  };
  const ownerFilter = sp.get("owner") || "";
  const filtroAtrasadas = sp.get("filtro") === "atrasadas";
  // LOTE AG-1: lembrar a última seleção de vendedor na aba Equipe. Ao
  // selecionar/limpar, grava/apaga; ao abrir a tela sem `owner=` na URL,
  // restaura automaticamente a última escolha (se existir).
  const AG_OWNER_KEY = "mpires_agenda_owner_v1";
  useEffect(() => {
    if (tab !== "equipe") return;
    if (ownerFilter) {
      try { localStorage.setItem(AG_OWNER_KEY, ownerFilter); } catch { /* ignore */ }
    } else {
      try {
        const salvo = localStorage.getItem(AG_OWNER_KEY);
        if (salvo) {
          const next = new URLSearchParams(sp);
          next.set("owner", salvo);
          setSp(next, { replace: true });
        }
      } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, ownerFilter]);
  const selecionarVendedor = (oid) => {
    const next = new URLSearchParams(sp);
    next.set("owner", oid);
    setSp(next, { replace: true });
  };
  const limparVendedor = () => {
    const next = new URLSearchParams(sp);
    next.delete("owner");
    setSp(next, { replace: true });
    try { localStorage.removeItem(AG_OWNER_KEY); } catch { /* ignore */ }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ titulo: "", tipo: "Ligação", data: "", descricao: "", owner_id: "", cliente_id: "" });
  const [clientes, setClientes] = useState([]);
  const [action, setAction] = useState(null); // Minha agenda: atividade + ações
  const [teamAction, setTeamAction] = useState(null); // Equipe: só leitura + cobrar/feedback
  const [teamActionExtra, setTeamActionExtra] = useState(null); // {ultimaInteracao, deal}
  const [cobrOpen, setCobrOpen] = useState(false);
  const [cobrText, setCobrText] = useState("");
  const [fbOpen, setFbOpen] = useState(false);
  const [fbText, setFbText] = useState("");
  const [vendedores, setVendedores] = useState([]);
  const [reagendarData, setReagendarData] = useState("");
  const [reagDiverg, setReagDiverg] = useState(null); // {texto, campo}
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useScrollRestore(!loading && !error);
  const openDeal = (id) => { saveCurrentScroll(); navigate(`/deal/${id}`); };
  const openCliente = (id) => { saveCurrentScroll(); navigate(`/cliente/${id}`); };

  useEffect(() => {
    const novo = sp.get("novo");
    if (novo) {
      // Não aceita mais "Próximo contato" / "Retorno" como tipo padrão (item 3 do pedido).
      const seguros = TIPOS;
      setForm((f) => ({ ...f, tipo: seguros.includes(novo) ? novo : "Ligação" }));
      setOpen(true);
      sp.delete("novo");
      setSp(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carrega clientes só quando abre o dialog (Nova atividade agora exige cliente).
  useEffect(() => {
    if (!open || clientes.length) return;
    api.get("/clientes").then((r) => setClientes(r.data || [])).catch(() => {});
  }, [open, clientes.length]);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await api.get("/agenda"); setData(r.data);
      if (isManager) {
        const [t, eq] = await Promise.all([
          api.get("/agenda?scope=equipe"),
          api.get("/equipe"),
        ]);
        setTeam(t.data);
        setVendedores((eq.data || []).filter((m) => m.role === "seller" || m.tipo === "vendedor"));
      }
    } catch { setError(true); } finally { setLoading(false); }
  }, [isManager]);
  useEffect(() => { load(); }, [load]);

  const abrirTeamAction = async (a) => {
    setTeamAction(a);
    setTeamActionExtra(null);
    if (a?.deal_id) {
      try {
        const [cmts, deals] = await Promise.all([
          api.get(`/deals/${a.deal_id}/comments`),
          api.get(`/deals`),
        ]);
        const arr = (cmts.data || []).filter((c) => (c.kind || "comment") === "comment");
        setTeamActionExtra({
          ultimaInteracao: arr.length ? arr[arr.length - 1] : null,
          deal: (deals.data || []).find((d) => d.id === a.deal_id) || null,
        });
      } catch { /* ignore */ }
    }
  };

  const cobrarNaAtividade = async () => {
    if (!teamAction?.deal_id || !cobrText.trim()) { toast.error("Informe a solicitação."); return; }
    try {
      await api.post(`/deals/${teamAction.deal_id}/cobrancas`, { text: cobrText.trim() });
      toast.success("Solicitação enviada");
      setCobrOpen(false); setCobrText(""); setTeamAction(null);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const feedbackNaAtividade = async () => {
    if (!teamAction?.deal_id || !fbText.trim()) { toast.error("Escreva o feedback."); return; }
    try {
      await api.post(`/deals/${teamAction.deal_id}/feedback`, { text: fbText.trim() });
      toast.success("Feedback enviado");
      setFbOpen(false); setFbText(""); setTeamAction(null);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openAction = (a) => {
    // Defesa: Concluir/Reagendar só para atividade do próprio usuário logado.
    // Se pertence a outro vendedor (só possível para gestor/diretor), abre
    // o fluxo de cobrança em vez de Concluir/Reagendar.
    if (a?.owner_id && user?.id && a.owner_id !== user.id) {
      abrirTeamAction(a);
      return;
    }
    setAction(a);
    setReagendarData(toLocalInput(a.data));
  };

  const concluir = async () => {
    if (!action) return;
    setBusy(true);
    try {
      await api.patch(`/atividades/${action.id}/toggle`);
      toast.success("Atividade concluída");
      setAction(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const _executarReagendar = async (novaData) => {
    if (!action) return;
    setBusy(true);
    try {
      await api.patch(`/atividades/${action.id}`, { data: novaData.toISOString() });
      toast.success("Atividade reagendada");
      setAction(null); setReagDiverg(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const reagendar = async () => {
    if (!action || !reagendarData) { toast.error("Informe a nova data e hora"); return; }
    // Aviso de divergência: se o texto da atividade (descrição/título)
    // menciona uma data diferente da nova data agendada, pergunta.
    const textoAtiv = `${action.descricao || ""} ${action.titulo || ""}`;
    const dTexto = extractDateFromText(textoAtiv);
    const dCampo = new Date(reagendarData);
    if (dTexto && !isSameLocalDay(dTexto, dCampo)) {
      setReagDiverg({ texto: dTexto, campo: dCampo });
      return;
    }
    await _executarReagendar(dCampo);
  };

  const irPara = () => {
    if (!action) return;
    const a = action; setAction(null);
    if (a.deal_id) openDeal(a.deal_id);
    else if (a.cliente_id) openCliente(a.cliente_id);
    else toast.info("Atividade sem oportunidade/cliente vinculado.");
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.cliente_id) { toast.error("Escolha o cliente da atividade."); return; }
    setSaving(true);
    try {
      const cli = clientes.find((c) => c.id === form.cliente_id);
      const payload = {
        ...form,
        cliente_nome: cli?.nome || cli?.empresa || "",
        data: form.data ? new Date(form.data).toISOString() : new Date().toISOString(),
      };
      if (!payload.owner_id) delete payload.owner_id;
      await api.post("/atividades", payload);
      toast.success("Atividade criada");
      setOpen(false); setForm({ titulo: "", tipo: "Ligação", data: "", descricao: "", owner_id: "", cliente_id: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={load} />;

  const total = (data.atrasadas?.length || 0) + (data.hoje?.length || 0) + (data.proximas?.length || 0);

  return (
    <div>
      <PageHeader
        title="Agenda comercial"
        subtitle={`${(data.counts?.concluidas_hoje || 0) === 1 ? "1 concluída hoje" : `${data.counts?.concluidas_hoje || 0} concluídas hoje`}`}
        action={
          <button data-testid="new-ativ-btn" onClick={() => setOpen(true)}
            aria-label="Nova atividade"
            className="flex items-center justify-center gap-2 h-11 w-11 md:h-10 md:w-auto md:px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors">
            <Plus className="w-4 h-4" /><span className="hidden md:inline">Nova atividade</span>
          </button>
        }
      />

      {/* Nova atividade — AppModal padrão */}
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title="Nova atividade"
        size="md"
        testId="nova-ativ-modal"
        footer={(
          <>
            <AppModalSecondaryButton onClick={() => setOpen(false)} disabled={saving} data-testid="nova-ativ-cancelar">
              Cancelar
            </AppModalSecondaryButton>
            <AppModalPrimaryButton type="submit" form="ativ-form" disabled={saving} data-testid="save-ativ-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Criar
            </AppModalPrimaryButton>
          </>
        )}
      >
        <form id="ativ-form" onSubmit={create} className="space-y-3" data-testid="ativ-form">
          <select
            required
            value={form.cliente_id}
            onChange={(e) => setForm((f) => ({ ...f, cliente_id: e.target.value }))}
            className={INPUT}
            data-testid="ativ-cliente"
          >
            <option value="">— Selecione o cliente —</option>
            {clientes.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}{c.empresa ? ` · ${c.empresa}` : ""}</option>
            ))}
          </select>
          <input required placeholder="Título" value={form.titulo} onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))} className={INPUT} data-testid="ativ-titulo" />
          <select value={form.tipo} onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))} className={INPUT} data-testid="ativ-tipo">
            {TIPOS.map((t) => <option key={t}>{t}</option>)}
          </select>
          <input type="datetime-local" value={form.data} onChange={(e) => setForm((f) => ({ ...f, data: e.target.value }))} className={INPUT} data-testid="ativ-data" />
          {isManager && vendedores.length > 0 ? (
            <select value={form.owner_id} onChange={(e) => setForm((f) => ({ ...f, owner_id: e.target.value }))} className={INPUT} data-testid="ativ-owner">
              <option value="">Atribuir a mim ({user?.name || "eu"})</option>
              {vendedores.map((v) => <option key={v.id} value={v.id}>{v.nome || v.name}</option>)}
            </select>
          ) : null}
          <textarea placeholder="Observação (opcional)" value={form.descricao} onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))} rows={2} className={`${INPUT} h-auto py-2.5`} />
        </form>
      </AppModal>
      {total === 0 && !isManager ? (
        <EmptyState
          icon={CalendarX2}
          title="Agenda em dia"
          action={
            <button
              data-testid="empty-agenda-btn"
              onClick={() => { setForm((f) => ({ ...f, tipo: "Ligação" })); setOpen(true); }}
              className="flex items-center gap-2 h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors"
            >
              <CalendarClock className="w-4 h-4" /> Nova atividade
            </button>
          }
        />
      ) : isManager ? (
        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList>
            <TabsTrigger value="minha" data-testid="tab-minha-agenda">Minha agenda</TabsTrigger>
            <TabsTrigger value="equipe" data-testid="tab-agenda-equipe">Equipe {team?.counts?.atrasadas ? `(${team.counts.atrasadas} atrasadas)` : ""}</TabsTrigger>
          </TabsList>
          <TabsContent value="minha" className="mt-4">
            {total === 0 ? (
              <EmptyState
                icon={CalendarX2}
                title="Agenda em dia"
                action={
                  <button
                    data-testid="empty-agenda-btn"
                    onClick={() => { setForm((f) => ({ ...f, tipo: "Ligação" })); setOpen(true); }}
                    className="flex items-center gap-2 h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors"
                  >
                    <CalendarClock className="w-4 h-4" /> Nova atividade
                  </button>
                }
              />
            ) : (
              <>
                {filtroAtrasadas && (
                  <div className="mb-3 flex items-center gap-2 text-xs" data-testid="agenda-filtro-atrasadas">
                    <span className="inline-flex items-center gap-1 rounded-full bg-bad-soft text-bad px-2.5 py-1 font-semibold">
                      Filtro: Atrasadas
                    </span>
                    <button
                      onClick={() => { const n = new URLSearchParams(sp); n.delete("filtro"); setSp(n, { replace: true }); }}
                      className="text-muted-foreground hover:text-navy underline underline-offset-2"
                    >
                      Limpar filtro
                    </button>
                  </div>
                )}
                <Section title="Atrasadas" items={data.atrasadas || []} tone="text-bad" onOpen={openAction} />
                {!filtroAtrasadas && (
                  <>
                    <Section title="Hoje" items={data.hoje || []} tone="text-brand" onOpen={openAction} />
                    <Section title="Próximos 7 dias" items={data.proximas || []} tone="text-muted-foreground" onOpen={openAction} />
                  </>
                )}
              </>
            )}
          </TabsContent>
          <TabsContent value="equipe" className="mt-4">
            {(() => {
              const allGroups = team?.por_vendedor || [];
              const groups = allGroups.filter((g) => !ownerFilter || g.owner_id === ownerFilter);
              const target = ownerFilter ? groups[0] : null;
              // LOTE AG-1: sem seleção → resumo compacto por vendedor (1 linha
              // cada, ordenado por mais atrasadas), com clique selecionando o
              // vendedor; com seleção → detalhe só do escolhido.
              if (!ownerFilter) {
                if (allGroups.length === 0) {
                  return <EmptyState icon={CalendarX2} title="Equipe em dia" subtitle="Nenhuma pendência dos vendedores no momento." />;
                }
                return (
                  <div className="space-y-2" data-testid="agenda-equipe-resumo">
                    <div className="text-xs text-muted-foreground mb-2">Toque num vendedor para ver as atividades dele.</div>
                    {allGroups.map((g) => (
                      <button
                        key={g.owner_id}
                        onClick={() => selecionarVendedor(g.owner_id)}
                        data-testid={`agenda-equipe-resumo-${g.owner_id}`}
                        className="w-full text-left bg-card rounded-xl border border-black/5 p-4 flex items-center gap-3 hover:border-brand/30"
                      >
                        <User className="w-5 h-5 text-brand shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-navy truncate">{g.owner_name}</div>
                          <div className="text-xs text-muted-foreground">
                            <span className={g.atrasadas.length ? "text-bad font-semibold" : ""}>{g.atrasadas.length} atrasadas</span>
                            <span> · </span>
                            <span>{g.pendentes.length} pendentes</span>
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-300" />
                      </button>
                    ))}
                  </div>
                );
              }
              return (
                <>
                  {ownerFilter && (
                    <div className="mb-3 flex items-center gap-2 text-xs" data-testid="agenda-owner-filter">
                      <span className="inline-flex items-center gap-1 rounded-full bg-brand-soft text-brand-hover px-2.5 py-1 font-semibold">
                        <User className="w-3 h-3" /> Vendedor: {target?.owner_name || "—"}
                      </span>
                      <button
                        onClick={limparVendedor}
                        data-testid="agenda-owner-clear"
                        className="text-muted-foreground hover:text-navy underline underline-offset-2"
                      >
                        Trocar vendedor
                      </button>
                    </div>
                  )}
                  {groups.length === 0 ? (
                    <EmptyState icon={CalendarX2} title="Equipe em dia" subtitle="Nenhuma pendência deste vendedor no momento." />
                  ) : (
                    groups.map((g) => (
                <div key={g.owner_id} className="mb-6" data-testid={`agenda-equipe-${g.owner_id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-brand" />
                    <span className="text-sm font-semibold text-navy">{g.owner_name}</span>
                    <span className="text-xs text-muted-foreground">· {g.atrasadas.length} atrasadas · {g.pendentes.length} pendentes</span>
                  </div>
                  {g.atrasadas.length ? (
                    <>
                      <div className="text-xs font-semibold uppercase tracking-wider text-bad mt-2 mb-1">Atrasadas</div>
                      <div className="space-y-2">
                        {g.atrasadas.map((a) => (
                          <button key={a.id} onClick={() => abrirTeamAction(a)} data-testid={`ativ-team-${a.id}`} className="w-full text-left bg-card rounded-xl border border-black/5 p-4 flex items-center gap-3 hover:border-warn/40">
                            <Circle className="w-5 h-5 text-bad shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-navy">{a.titulo}</div>
                              <div className="text-xs text-muted-foreground">{a.tipo}{a.cliente_nome ? ` · ${a.cliente_nome}` : ""} · {fmtDateTime(a.data)} · <span className="text-bad font-semibold">{a.dias_atraso}d atraso</span></div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-slate-300" />
                          </button>
                        ))}
                      </div>
                    </>
                  ) : null}
                  {g.pendentes.length ? (
                    <>
                      <div className="text-xs font-semibold uppercase tracking-wider text-brand mt-3 mb-1">Pendentes</div>
                      <div className="space-y-2">
                        {g.pendentes.map((a) => (
                          <button key={a.id} onClick={() => abrirTeamAction(a)} data-testid={`ativ-team-${a.id}`} className="w-full text-left bg-card rounded-xl border border-black/5 p-4 flex items-center gap-3 hover:border-brand/30">
                            <Circle className="w-5 h-5 text-slate-300 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-navy">{a.titulo}</div>
                              <div className="text-xs text-muted-foreground">{a.tipo}{a.cliente_nome ? ` · ${a.cliente_nome}` : ""} · {fmtDateTime(a.data)}</div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-slate-300" />
                          </button>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
              ))
                  )}
                </>
              );
            })()}
          </TabsContent>
        </Tabs>
      ) : (
        <>
          <Section title="Atrasadas" items={data.atrasadas || []} tone="text-bad" onOpen={openAction} />
          <Section title="Hoje" items={data.hoje || []} tone="text-brand" onOpen={openAction} />
          <Section title="Próximos 7 dias" items={data.proximas || []} tone="text-muted-foreground" onOpen={openAction} />
        </>
      )}

      {/* Ações da atividade (Concluir / Reagendar) — padrão AppModal */}
      <AppModal
        open={!!action}
        onOpenChange={(o) => !o && setAction(null)}
        title={action?.titulo || "Atividade"}
        subtitle={action ? `${action.tipo}${action.cliente_nome ? " · " + action.cliente_nome : ""} · ${fmtDateTime(action.data)}` : undefined}
        size="sm"
        testId="ativ-acoes-modal"
        footer={action ? (
          <>
            <AppModalSecondaryButton onClick={() => setAction(null)} disabled={busy} data-testid="ativ-cancelar">
              Cancelar
            </AppModalSecondaryButton>
            <AppModalSecondaryButton onClick={concluir} disabled={busy} data-testid="ativ-concluir-btn">
              <CheckCircle2 className="w-4 h-4" style={{ color: "#1E7A55" }} /> Concluir
            </AppModalSecondaryButton>
            <AppModalPrimaryButton onClick={reagendar} disabled={busy || !reagendarData} data-testid="ativ-reagendar-btn">
              <CalendarClock className="w-4 h-4" /> Reagendar
            </AppModalPrimaryButton>
          </>
        ) : null}
      >
        {action ? (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reagendar para</label>
              <input
                type="datetime-local"
                value={reagendarData}
                onChange={(e) => setReagendarData(e.target.value)}
                className={INPUT + " mt-1"}
                data-testid="ativ-reagendar-data"
              />
            </div>
            {(action.deal_id || action.cliente_id) ? (
              <button
                type="button"
                onClick={irPara}
                data-testid="ativ-abrir-vinculo"
                className="w-full h-10 rounded-lg bg-transparent text-brand text-sm font-semibold hover:bg-brand-soft/50"
              >
                {action.deal_id ? "Abrir oportunidade" : "Abrir cliente"}
              </button>
            ) : null}
          </div>
        ) : null}
      </AppModal>

      {/* Menu de ação sobre atividade da EQUIPE (só leitura + cobrar/feedback) */}
      <Dialog open={!!teamAction} onOpenChange={(o) => !o && setTeamAction(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{teamAction?.titulo || "Atividade da equipe"}</DialogTitle></DialogHeader>
          {teamAction ? (
            <div className="space-y-3 text-sm">
              <div className="text-muted-foreground">
                {teamAction.tipo}{teamAction.owner_name ? ` · ${teamAction.owner_name}` : ""}{teamAction.cliente_nome ? ` · ${teamAction.cliente_nome}` : ""} · {fmtDateTime(teamAction.data)}
                {teamAction.dias_atraso ? <span className="text-bad font-semibold"> · {teamAction.dias_atraso}d atraso</span> : null}
              </div>
              {teamActionExtra?.ultimaInteracao ? (
                <div className="rounded-lg bg-slate-50 border border-black/5 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Última interação registrada</div>
                  <div className="mt-1 text-xs text-muted-foreground">{teamActionExtra.ultimaInteracao.author_name} · {fmtDateTime(teamActionExtra.ultimaInteracao.created_at)}</div>
                  <div className="mt-1 text-sm text-navy">{teamActionExtra.ultimaInteracao.text}</div>
                </div>
              ) : teamAction.deal_id ? (
                <div className="rounded-lg bg-slate-50 border border-black/5 p-3 text-xs text-muted-foreground">Sem interações do responsável registradas ainda.</div>
              ) : null}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => { if (!teamAction.deal_id) { toast.error("Atividade sem oportunidade vinculada."); return; } setCobrText(""); setCobrOpen(true); }}
                  data-testid="ativ-team-cobrar"
                  className="h-11 rounded-lg bg-warn text-white text-sm font-semibold hover:bg-warn/90 flex items-center justify-center gap-2"
                ><Megaphone className="w-4 h-4" /> Pedir retorno</button>
                <button
                  onClick={() => { if (teamAction.deal_id) openDeal(teamAction.deal_id); else if (teamAction.cliente_id) openCliente(teamAction.cliente_id); setTeamAction(null); }}
                  data-testid="ativ-team-abrir"
                  className="h-11 rounded-lg border border-input text-navy text-sm font-medium hover:bg-slate-50 flex items-center justify-center gap-2"
                ><ArrowRight className="w-4 h-4" /> Abrir oportunidade</button>
              </div>
              {teamAction.concluida && teamAction.tipo === "Visita" ? (
                <button
                  onClick={() => { if (!teamAction.deal_id) { toast.error("Sem oportunidade vinculada."); return; } setFbText(""); setFbOpen(true); }}
                  data-testid="ativ-team-feedback"
                  className="w-full h-11 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover flex items-center justify-center gap-2"
                ><MessageSquare className="w-4 h-4" /> Dar feedback</button>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Diálogo: cobrança do gestor a partir da agenda */}
      <Dialog open={cobrOpen} onOpenChange={setCobrOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Pedir retorno</DialogTitle></DialogHeader>
          <textarea rows={4} value={cobrText} onChange={(e) => setCobrText(e.target.value)} className={INPUT + " h-auto py-2.5"} placeholder="Descreva a solicitação (obrigatório)" data-testid="ativ-team-cobrar-texto" />
          <DialogFooter>
            <button onClick={cobrarNaAtividade} disabled={!cobrText.trim()} data-testid="ativ-team-cobrar-enviar" className="h-10 px-5 rounded-lg bg-warn text-white text-sm font-semibold hover:bg-warn/90 disabled:opacity-60">Enviar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: feedback pós-visita */}
      <Dialog open={fbOpen} onOpenChange={setFbOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Feedback da visita</DialogTitle></DialogHeader>
          <textarea rows={4} value={fbText} onChange={(e) => setFbText(e.target.value)} className={INPUT + " h-auto py-2.5"} placeholder="O que precisa evoluir ou reforçar" data-testid="ativ-team-feedback-texto" />
          <DialogFooter>
            <button onClick={feedbackNaAtividade} disabled={!fbText.trim()} data-testid="ativ-team-feedback-enviar" className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-60">Enviar feedback</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Divergência de data no Reagendar — componente compartilhado */}
      <DivergenciaDataDialog
        open={!!reagDiverg}
        onClose={() => setReagDiverg(null)}
        dataTexto={reagDiverg?.texto}
        dataCampo={reagDiverg?.campo}
        testId="ativ-reag-div-dialog"
        onManter={() => { const c = reagDiverg.campo; setReagDiverg(null); _executarReagendar(c); }}
        onUsarTexto={() => {
          const base = reagDiverg.campo;
          const nova = new Date(
            reagDiverg.texto.getFullYear(), reagDiverg.texto.getMonth(), reagDiverg.texto.getDate(),
            base.getHours(), base.getMinutes(), 0, 0
          );
          setReagDiverg(null);
          _executarReagendar(nova);
        }}
      />
    </div>
  );
}
